import { ComponentProps } from 'react';
import { css } from '@emotion/react';
import { AnalyticsConstants } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Dot, ModalControlResponse, Text, TextLink } from '@frontend/design-system';
import { PhoneTreePhoneNumbersTrayType } from '../shared-types';
import { TransformedChartData } from '../utils/transform-phone-tree-chart-data';

export type DialOptionNumber = `dialOption${0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`;

type DialOptions = {
  [key in DialOptionNumber | 'fallback' | 'abandoned' | 'extension' | 'unknown']: string;
};

export type DialOption = keyof DialOptions;

type DialOptionWithoutUnknown = Exclude<DialOption, 'unknown'>;

type DialOptionWithValues = Record<DialOptionWithoutUnknown, string>;

const colors: DialOptionWithValues = {
  dialOption0: theme.colors.secondary.eggplant30,
  dialOption1: theme.colors.secondary.seaweed30,
  dialOption2: theme.colors.primary30,
  dialOption3: theme.colors.success30,
  dialOption4: theme.colors.tangerine30,
  dialOption5: theme.colors.rose30,
  dialOption6: theme.colors.indigo30,
  dialOption7: theme.colors.critical30,
  dialOption8: theme.colors.secondary.seaweed40,
  dialOption9: theme.colors.tangerine40,
  fallback: theme.colors.neutral30,
  extension: theme.colors.primary40,
  abandoned: theme.colors.warning20,
};

const toolTipColors: DialOptionWithValues = {
  dialOption0: theme.colors.secondary.eggplant40,
  dialOption1: theme.colors.secondary.seaweed40,
  dialOption2: theme.colors.primary40,
  dialOption3: theme.colors.success40,
  dialOption4: theme.colors.tangerine40,
  dialOption5: theme.colors.rose40,
  dialOption6: theme.colors.indigo40,
  dialOption7: theme.colors.critical40,
  dialOption8: theme.colors.secondary.seaweed40,
  dialOption9: theme.colors.tangerine40,
  fallback: theme.colors.neutral40,
  extension: theme.colors.primary40,
  abandoned: theme.colors.warning40,
};

const labels: DialOptionWithValues = {
  dialOption0: i18next.t('Dial Option: 0', { ns: 'analytics-phone' }),
  dialOption1: i18next.t('Dial Option: 1', { ns: 'analytics-phone' }),
  dialOption2: i18next.t('Dial Option: 2', { ns: 'analytics-phone' }),
  dialOption3: i18next.t('Dial Option: 3', { ns: 'analytics-phone' }),
  dialOption4: i18next.t('Dial Option: 4', { ns: 'analytics-phone' }),
  dialOption5: i18next.t('Dial Option: 5', { ns: 'analytics-phone' }),
  dialOption6: i18next.t('Dial Option: 6', { ns: 'analytics-phone' }),
  dialOption7: i18next.t('Dial Option: 7', { ns: 'analytics-phone' }),
  dialOption8: i18next.t('Dial Option: 8', { ns: 'analytics-phone' }),
  dialOption9: i18next.t('Dial Option: 9', { ns: 'analytics-phone' }),
  fallback: i18next.t('Fallback', { ns: 'analytics-phone' }),
  extension: i18next.t('Extension Dialing', { ns: 'analytics-phone' }),
  abandoned: i18next.t('Abandoned', { ns: 'analytics-phone' }),
};

type Props = ComponentProps<typeof Chart> &
  TransformedChartData & {
    triggerProps: ModalControlResponse['triggerProps'];
    setModalData: (val: PhoneTreePhoneNumbersTrayType) => void;
    phoneNumbers?: { locationName: string; number: string }[];
  };

export const PhoneTreeCallVolumeChart = ({
  updatedAt,
  phoneTreeName,
  totalCalls,
  phoneTreeId,
  abandonedCalls,
  callRouteName,
  callRouteId,
  phoneNumbers,
  groups,
  triggerProps,
  setModalData,
  ...rest
}: Props) => {
  const { t } = useTranslation('analytics-phone');

  const openTray = () => {
    triggerProps.onClick();
    setModalData({ phoneTreeName, callRouteName, callRouteId, phoneNumbers });
  };

  const numOfPhoneNumbers = phoneNumbers?.length ?? 0;

  return (
    <Chart
      colors={colors}
      isLoading={false}
      labels={labels}
      {...rest}
      emptyStateConfig={{
        type: 'missing_forms',
        label: t('No data available'),
      }}
    >
      <Chart.Header
        bottomElement={
          numOfPhoneNumbers > 0 && (
            <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
              <Text size='medium'>{t('Phone Number(s): {{value}}', { value: phoneNumbers?.[0].number })}</Text>
              {numOfPhoneNumbers > 1 && (
                <TextLink onClick={openTray} color='primary'>
                  {t('+{{count}} more', { count: phoneNumbers ? phoneNumbers.length - 1 : 0 })}
                </TextLink>
              )}
            </div>
          )
        }
        title={phoneTreeName ?? ''}
        css={css`
          .title-wrapper > .heading {
            font-size: ${theme.font.size.large};
          }
        `}
      />
      <Chart.PieChart
        css={css`
          align-items: start;
        `}
        appearance={{
          width: AnalyticsConstants.DASHBOARD_CHART_SIZE + 20,
          height: AnalyticsConstants.DASHBOARD_CHART_SIZE + 20,
          innerRadius: 55,
          outerRadius: 95,
          paddingAngle: 4,
          customTooltipData: (id) => {
            const numOfCalls = groups?.find((g) => g.name === id)?.value ?? 0;
            return [
              {
                id,
                color: toolTipColors[id as keyof typeof colors],
                formattedValue: numOfCalls,
                label: t('Calls', { count: numOfCalls }),
              },
              {
                id,
                color: toolTipColors[id as keyof typeof colors],
                formattedValue:
                  !!totalCalls && totalCalls > 0 ? Math.round((numOfCalls / totalCalls) * 100) + '%' : '0% ',
                label: t('of Calls to This Phone Tree'),
              },
            ];
          },
          customTooltipTitle: (args) => {
            return (
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Dot
                  color='primary'
                  css={{
                    position: 'relative',
                    border: 0,
                    width: '10px',
                    height: '10px',
                    backgroundColor: colors[args.hoveredSegment as keyof typeof colors],
                  }}
                />
                <Text size='small'>{labels[args.hoveredSegment as keyof typeof labels]}</Text>
              </div>
            );
          },
        }}
        data={{
          centerMetric: totalCalls,
          centerMetricLabel: t('Total Calls'),
          groups,
        }}
      />
      <Chart.Legends highlightOnSegmentHover />
    </Chart>
  );
};
